import { defineStore } from "pinia";
import { getCurrentAccount, type AccountPet, type AccountState, type PaymentQuota } from "../generation-api";
import { appLocale, t } from "../i18n";
import { buildApiUrl, runtimeConfig } from "../runtime-config";

export interface AuthUser {
  userId: string;
  region: "CN" | "GLOBAL";
  email?: string | null;
  nickname?: string | null;
  avatarUrl?: string | null;
}

export type AuthQuota = PaymentQuota;

interface LoginResponse {
  token: string;
  user: AuthUser;
  quota: AuthQuota;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null as AuthUser | null,
    token: localStorage.getItem("pet.auth.token") || "",
    quota: null as AuthQuota | null,
    pets: [] as AccountPet[]
  }),
  getters: {
    isLoggedIn: (state) => Boolean(state.user && state.token),
    hasStoredToken: (state) => Boolean(state.token)
  },
  actions: {
    async login(email: string, password: string) {
      const normalizedEmail = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        throw new Error(t("auth.invalidEmail"));
      }
      if (!password.trim()) {
        throw new Error(t("auth.passwordRequired"));
      }

      const response = await loginWithBackend(normalizedEmail, password);

      this.user = response.user;
      this.token = response.token;
      this.quota = response.quota;
      this.pets = [];
      localStorage.setItem("pet.auth.token", response.token);
    },
    async restoreSession(): Promise<boolean> {
      if (!this.token) {
        return false;
      }
      if (this.user && this.quota) {
        return true;
      }

      try {
        const account = await this.refreshAccount();
        return Boolean(account?.user);
      } catch {
        this.logout();
        return false;
      }
    },
    acceptExternalToken(token: string) {
      const normalizedToken = token.trim();
      if (!normalizedToken) {
        return;
      }

      this.token = normalizedToken;
      localStorage.setItem("pet.auth.token", normalizedToken);
    },
    async refreshAccount(): Promise<AccountState | null> {
      if (!this.token) {
        return null;
      }

      const account = await getCurrentAccount();
      this.user = account.user;
      this.quota = account.quota;
      this.pets = account.pets;
      return account;
    },
    async changePassword(currentPassword: string, newPassword: string) {
      if (!currentPassword.trim()) {
        throw new Error(t("auth.currentPasswordRequired"));
      }
      if (!newPassword.trim()) {
        throw new Error(t("auth.newPasswordRequired"));
      }

      await changePasswordWithBackend(currentPassword, newPassword);
    },
    logout() {
      this.user = null;
      this.token = "";
      this.quota = null;
      this.pets = [];
      clearJwtStorage(localStorage);
      if (typeof sessionStorage !== "undefined") {
        clearJwtStorage(sessionStorage);
      }
    }
  }
});

async function loginWithBackend(email: string, password: string): Promise<LoginResponse> {
  const response = await safeAuthFetch("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ email, password, region: runtimeConfig.market.region })
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.message || `HTTP ${response.status}`);
  }
  return json as LoginResponse;
}

async function changePasswordWithBackend(currentPassword: string, newPassword: string): Promise<void> {
  const response = await safeAuthFetch("/api/auth/password", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(localStorage.getItem("pet.auth.token")
        ? { Authorization: `Bearer ${localStorage.getItem("pet.auth.token")}` }
        : {})
    },
    body: JSON.stringify({ currentPassword, newPassword })
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.message || `HTTP ${response.status}`);
  }
}

async function safeAuthFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set("X-App-Locale", appLocale);
  headers.set("X-App-Region", runtimeConfig.market.region);
  try {
    return await fetch(buildApiUrl(path), {
      ...init,
      headers
    });
  } catch {
    throw new Error(t("store.network"));
  }
}

function clearJwtStorage(storage: Storage): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) {
      continue;
    }
    const normalizedKey = key.toLowerCase();
    if (normalizedKey === "pet.auth.token" || normalizedKey.startsWith("pet.auth.") || normalizedKey.includes("jwt")) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}
